import React, { useState, useEffect } from 'react';
import { 
  Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Badge, Spinner, AlertDialog, AlertDialogContent, 
  AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '../ui/UIComponents';
import { Plus, Edit, Trash2, Shield, Settings, AlertCircle, Users, ChevronDown, Layers, Globe } from 'lucide-react';
import { Role, Application } from '../../types/rbac.types';
import { getRoles, deleteRole } from '../../services/rbacApi';
import { getApplications } from '../../services/rbacApi';
import RoleForm from './RoleForm';
import RolePermissions from './RolePermissions';
import TreeView, { TreeNode } from '../ui/TreeView';

const RoleList: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [deleteErrorDetails, setDeleteErrorDetails] = useState<any>(null);

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Fetch roles on component mount or when application filter changes
  useEffect(() => {
    fetchRoles(selectedAppId);
  }, [selectedAppId]);

  const fetchApplications = async () => {
    try {
      const data = await getApplications();
      // Applications fetched successfully
      setApplications(data);
      
      // We don't auto-select an application anymore as roles are application-agnostic
      // But we keep the applications list for optional filtering
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch applications');
      console.error('Error fetching applications:', err);
    }
  };

  const fetchRoles = async (applicationId?: string) => {
    try {
      setLoading(true);
      const data = await getRoles(applicationId, true); // Include permissions
      setRoles(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch roles');
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = (appId: string) => {
    // Application selected
    setSelectedAppId(appId);
  };
  
  // Convert applications to tree nodes
  const getApplicationTreeNodes = (): TreeNode[] => {
    const allAppsNode: TreeNode = {
      id: '',
      name: 'All Applications',
      icon: <Globe size={18} className="text-green-400" />,
    };
    
    const appNodes: TreeNode[] = applications.map(app => ({
      id: app.id,
      name: app.name,
      icon: <Layers size={18} className="text-blue-400" />,
      data: app,
      badge: app.status === 'active' ? (
        <Badge variant="secondary" className="bg-green-900/30 text-green-300 border border-green-700/30 text-xs">
          Active
        </Badge>
      ) : app.status === 'development' ? (
        <Badge variant="secondary" className="bg-amber-900/30 text-amber-300 border border-amber-700/30 text-xs">
          Dev
        </Badge>
      ) : (
        <Badge variant="secondary" className="bg-gray-900/30 text-gray-300 border border-gray-700/30 text-xs">
          Inactive
        </Badge>
      )
    }));
    
    return [allAppsNode, ...appNodes];
  };

  const handleEdit = (role: Role) => {
    setCurrentRole(role);
    setIsFormOpen(true);
  };

  const handleDelete = (role: Role) => {
    setRoleToDelete(role);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!roleToDelete) return;
    
    const result = await deleteRole(roleToDelete.id);
    
    if (result.success) {
      // Refresh the roles list
      fetchRoles(selectedAppId);
      setIsDeleteDialogOpen(false);
      setRoleToDelete(null);
      setDeleteErrorDetails(null);
    } else {
      // Show error message
      setError(result.message || 'Failed to delete role');
      setDeleteErrorDetails(result.details);
      // Keep the dialog open to show the error
    }
  };

  const handleFormClose = (refreshData: boolean = false) => {
    setIsFormOpen(false);
    setCurrentRole(null);
    if (refreshData && selectedAppId) {
      fetchRoles(selectedAppId);
    }
  };

  const handleViewPermissions = (role: Role) => {
    setSelectedRole(role);
    setIsPermissionsOpen(true);
  };

  const handlePermissionsClose = (refreshData: boolean = false) => {
    setIsPermissionsOpen(false);
    setSelectedRole(null);
    if (refreshData && selectedAppId) {
      fetchRoles(selectedAppId);
    }
  };

  // Find the selected application name
  const selectedAppName = applications.find(app => app.id === selectedAppId)?.name || '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200">Roles</h2>
          <p className="text-gray-400 text-sm mt-1">Manage roles and their permissions</p>
        </div>
        <Button 
          variant="primary" 
          size="sm" 
          onClick={() => setIsFormOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
        >
          <Plus size={16} className="mr-1.5" />
          Add Role
        </Button>
      </div>

      <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 p-0.5 rounded-lg overflow-hidden">
        <div className="bg-background-card rounded-lg p-5">
          <label className="block text-sm font-medium text-blue-300 mb-2 flex items-center">
            <Settings size={16} className="mr-2" />
            Filter by Application (Optional)
          </label>
          <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/50">
            <TreeView 
              nodes={getApplicationTreeNodes()} 
              onSelect={(node) => handleApplicationChange(node.id)}
              selectedId={selectedAppId}
              className="max-h-[250px] overflow-y-auto py-1.5"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex items-center space-x-2 animate-fadeIn">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="relative w-16 h-16">
            <div className="absolute top-0 right-0 h-16 w-16 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
            <div className="absolute top-1 right-1 h-14 w-14 rounded-full border-4 border-t-transparent border-r-indigo-500 border-b-transparent border-l-transparent animate-spin-slow"></div>
          </div>
          <p className="text-gray-400 mt-4">Loading roles...</p>
        </div>
      ) : roles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-b from-gray-900/30 to-gray-900/10 rounded-xl border border-gray-800/50">
          <div className="bg-blue-900/20 p-4 rounded-full border border-blue-500/20 mb-4">
            <Users className="h-10 w-10 text-blue-400/80" />
          </div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">No roles found</h3>
          <p className="text-gray-400 mb-6 max-w-md text-center">
            {selectedAppId ? 
              <>No roles have been created for <span className="text-blue-300 font-medium">{selectedAppName}</span> yet.</> : 
              <>No roles have been created yet. Click below to add your first role.</>
            }
          </p>
          <Button 
            variant="primary" 
            onClick={() => setIsFormOpen(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
          >
            <Plus size={16} className="mr-1.5" />
            Add your first role
          </Button>
        </div>
      ) : (
        <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 p-0.5 rounded-xl overflow-hidden">
          <div className="overflow-x-auto bg-background-card rounded-xl">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="font-medium text-gray-300">Name</TableCell>
                  <TableCell className="font-medium text-gray-300">Description</TableCell>
                  <TableCell className="font-medium text-gray-300">Application</TableCell>
                  <TableCell className="font-medium text-gray-300">Permissions</TableCell>
                  <TableCell className="font-medium text-gray-300">System Role</TableCell>
                  <TableCell className="font-medium text-gray-300">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} className="transition-colors hover:bg-gray-800/30">
                    <TableCell className="font-medium text-blue-300">{role.name}</TableCell>
                    <TableCell className="max-w-xs truncate text-gray-400">{role.description || '-'}</TableCell>
                    <TableCell>
                      {role.application_name ? (
                        <Badge variant="secondary" className="bg-indigo-900/30 text-indigo-300 border border-indigo-700/30">
                          {role.application_name}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-900/30 text-green-300 border border-green-700/30">
                          Global Role
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="primary" className="bg-blue-900/30 text-blue-300 border border-blue-700/30">
                        {role.permission_count || 0} permissions
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {role.is_system_role ? (
                        <Badge variant="secondary" className="bg-purple-900/30 text-purple-300 border border-purple-700/30">
                          System Role
                        </Badge>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="xs"
                          onClick={() => handleViewPermissions(role)}
                          title="Manage Permissions"
                          className="border-indigo-800/30 bg-indigo-900/20 hover:bg-indigo-800/30 text-indigo-300"
                        >
                          <Shield size={14} className="mr-1" />
                          Permissions
                        </Button>
                        <Button 
                          variant="outline" 
                          size="xs" 
                          onClick={() => handleEdit(role)}
                          title="Edit"
                          disabled={role.is_system_role}
                          className={role.is_system_role ? 
                            "opacity-50 cursor-not-allowed" : 
                            "border-blue-800/30 bg-blue-900/20 hover:bg-blue-800/30 text-blue-300"}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="xs"
                          onClick={() => handleDelete(role)}
                          title="Delete"
                          disabled={role.is_system_role}
                          className={role.is_system_role ? 
                            "opacity-50 cursor-not-allowed" : 
                            "border-red-800/30 bg-red-900/20 hover:bg-red-800/30 text-red-300"}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Role Form Modal */}
      <RoleForm 
        isOpen={isFormOpen} 
        onClose={handleFormClose} 
        role={currentRole} 
        applicationId={selectedAppId}
      />

      {/* Role Permissions Modal */}
      {selectedRole && (
        <RolePermissions
          isOpen={isPermissionsOpen}
          onClose={handlePermissionsClose}
          role={selectedRole}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border border-gray-800">
          <AlertDialogHeader>
            {deleteErrorDetails ? (
              <>
                <AlertDialogTitle className="text-red-400">Cannot Delete Role</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  {deleteErrorDetails.message || deleteErrorDetails.error || "This role cannot be deleted."}
                  
                  {deleteErrorDetails.users && deleteErrorDetails.users.length > 0 && (
                    <div className="mt-4">
                      <p className="font-medium text-gray-200 mb-2">This role is assigned to the following users:</p>
                      <div className="max-h-60 overflow-y-auto bg-gray-800/50 border border-gray-700 rounded-md p-2">
                        <ul className="list-disc pl-5 space-y-1">
                          {deleteErrorDetails.users.map((user: any) => (
                            <li key={user.id} className="text-gray-300">
                              {user.username} ({user.email})
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-gray-300 mt-3">
                        Please remove this role from these users before deleting.
                      </p>
                    </div>
                  )}
                </AlertDialogDescription>
              </>
            ) : (
              <>
                <AlertDialogTitle className="text-gray-100">Are you sure?</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-400">
                  This will delete the role "{roleToDelete?.name}". 
                  This action cannot be undone.
                </AlertDialogDescription>
              </>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-gray-800 text-gray-300 hover:bg-gray-700"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeleteErrorDetails(null);
              }}
            >
              {deleteErrorDetails ? "Close" : "Cancel"}
            </AlertDialogCancel>
            {!deleteErrorDetails && (
              <AlertDialogAction 
                onClick={confirmDelete} 
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
              >
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RoleList; 